import React, { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import PeopleIcon from "@mui/icons-material/People";
import KeyIcon from "@mui/icons-material/Key";
import SettingsIcon from "@mui/icons-material/Settings";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import AnalyticsIcon from "@mui/icons-material/Assessment";
import { useHistory, useLocation } from "react-router-dom";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PhonelinkSetupIcon from "@mui/icons-material/PhonelinkSetup";
import { makeStyles } from "@mui/styles";
import { theme } from "../../config/theme";

import { LO_STO } from "../../util/constants";
import { getCurrentUser } from "../../api/user";
import { getOrganizations } from "../../api/organization";
import { isSukiAdmin, isSukiUser as isSukiOrganizationUser } from "../../helpers/roles";

const useStyles = makeStyles({
  categoryHeader: {
    paddingTop: `${theme.spacing(2)}`,
    paddingBottom: `${theme.spacing(2)}`
  },
  categoryHeaderPrimary: {
    color: `${theme.palette.common.white}`
  },
  item: {
    "paddingTop": "1px",
    "paddingBottom": "1px",
    "color": "rgba(255, 255, 255, 0.7)",
    "&:hover,&:focus": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      outline: "none"
    }
  },
  itemCategory: {
    backgroundColor: "#1D2B36",
    boxShadow: "0 -1px 0 #404854 inset",
    paddingTop: `${theme.spacing(2)}`,
    paddingBottom: `${theme.spacing(2)}`
  },
  firebase: {
    fontSize: 24,
    color: `${theme.palette.common.white}`
  },
  itemActiveItem: {
    color: "#00C1AE"
  },
  itemPrimary: {
    fontSize: "14px"
  },
  itemIcon: {
    minWidth: "auto",
    marginRight: `${theme.spacing(2)}`
  },
  divider: {
    marginTop: `${theme.spacing(2)}`
  }
});

function Navigator(props) {
  const classes = useStyles();
  const { ...other } = props;

  const { pathname } = useLocation();
  const history = useHistory();

  const organizationId = window.localStorage.getItem(LO_STO.ORG_ID);

  const [organization, setOrganization] = useState({});
  const [isSukiUser, setIsSukiUser] = useState(isSukiOrganizationUser());
  const [organizationCategory, setOrganizationCategory] = useState({});
  const [menuCategories, setMenuCategories] = useState([]);

  const organizationRoute = "/organizations";
  const isTabsRoute = () => pathname.includes("users") || pathname.includes("tokens") || pathname.includes("analytics");

  const adminCategory = useMemo(
    () => ({
      id: isSukiAdmin() ? "Suki Admin" : organization?.name,
      children: [
        {
          id: "Organizations",
          icon: <SettingsIcon />,
          route: "/organizations"
        }
      ]
    }),
    [organization]
  );

  const getOrganizationCategory = (orgName = "Your Organization") => ({
    id: orgName,
    children: [
      {
        id: "Dashboard",
        icon: <AssessmentIcon />,
        active: true,
        route: "/dashboard"
      },
      {
        id: "Tokens",
        icon: <PhonelinkSetupIcon />,
        active: true,
        route: "/tokens"
      },
      {
        id: "API Keys",
        icon: <KeyIcon />,
        active: true,
        route: "/apikeys"
      },
      { id: "Users", icon: <PeopleIcon />, active: false, route: "/users" },
      {
        id: "Analytics",
        icon: <AnalyticsIcon />,
        active: false,
        route: "/analytics"
      }
    ]
  });

  const navigateToPage = (route) => {
    if (route) {
      history.push(route);
    }
  };

  const fetchUser = useCallback(async () => {
    await getCurrentUser().then(() => {
      setIsSukiUser(isSukiOrganizationUser());
    });
  }, []);

  const fetchOrganization = useCallback(() => {
    getOrganizations([organizationId]).then((organizations) => {
      const organization = organizations.find((o) => o.id === organizationId);
      setOrganization(organization);
    });
  }, [organizationId]);

  const isActiveRoute = (route) => {
    const isOrganizationPage = pathname === organizationRoute && route === organizationRoute;
    const isOrganizationDetailsPage = pathname.includes(route) && route === organizationRoute && isTabsRoute();
    const isOtherPage = pathname === route && pathname !== organizationRoute;
    return isOrganizationPage || isOrganizationDetailsPage || isOtherPage;
  };

  useEffect(() => {
    fetchUser();
    fetchOrganization();
  }, [fetchUser, fetchOrganization]);

  useEffect(() => {
    const organizationCategory = getOrganizationCategory(organization?.name);
    setOrganizationCategory(organizationCategory);
  }, [organization]);

  useEffect(() => {
    if (isSukiUser) {
      setMenuCategories([adminCategory, organizationCategory]);
    } else {
      setMenuCategories([organizationCategory]);
    }
  }, [isSukiUser, adminCategory, organizationCategory]);

  return (
    <Drawer className={classes.drawer} variant="permanent" {...other}>
      <List disablePadding>
        <ListItem key="header" className={clsx(classes.itemCategory, classes.item)}>
          <strong> SSP Dashboard </strong>
        </ListItem>
        {menuCategories &&
          menuCategories.map((category, categoryIndex) => (
            <React.Fragment key={`${category.id}-${categoryIndex}`}>
              <ListItem key={`${category.id}-header`} className={classes.categoryHeader}>
                <ListItemText
                  classes={{
                    primary: classes.categoryHeaderPrimary
                  }}
                >
                  {category.id}
                </ListItemText>
              </ListItem>
              {category.children &&
                category.children.map((child, childIndex) => (
                  <ListItem
                    key={`${child.id}-${childIndex}`}
                    button
                    className={clsx(isActiveRoute(child.route) ? classes.itemActiveItem : null, classes.item)}
                    onClick={() => navigateToPage(child.route)}
                  >
                    <ListItemIcon className={classes.itemIcon}>{child.icon}</ListItemIcon>
                    <ListItemText
                      classes={{
                        primary: classes.itemPrimary
                      }}
                    >
                      {child.id}
                    </ListItemText>
                  </ListItem>
                ))}
              <Divider key={`${category.id}-divider`} className={classes.divider} />
            </React.Fragment>
          ))}
      </List>
    </Drawer>
  );
}

Navigator.propTypes = {
  other: PropTypes.object
};

export default Navigator;
